import { useAuth0 } from '@auth0/auth0-react';
import { FC, useEffect } from 'react';
import { isTokenExpired } from '../../utils/isTokenExpired';
import refreshTokenOrLogout from '../../utils/refreshTokenOrLogout';
import { assessmentSubmitted } from '../../bffApiCalls/assessmentSubmitted';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import * as Sentry from '@sentry/react';
import { ExitSlipResult } from '../../models/ExitSlip';
import { usePostHog } from '@posthog/react';
import {
  useLearnosityItemsInitialization,
  type LearnosityInitializationRefetch,
} from '../../custom-hooks/useLearnosityItemsInitialization';
import { useSuspiciousActivityTracking } from '../../custom-hooks/useSuspiciousActivityTracking';
import { useExitFullscreenOnUnmount } from '../../custom-hooks/useExitFullscreenOnUnmount';
import { useCopyProtection } from '../../custom-hooks/useCopyProtection';

interface Props {
  request: LearnosityRequest;
  enabled?: boolean;
  refetchInitialization: LearnosityInitializationRefetch;
  onFailure?: (error: unknown) => void;
  sessionId: string;
  classExitSlipId: string;
  setLoading: (state: boolean) => void;
  setExitSlipResult: (result: ExitSlipResult[]) => void;
  skillId?: string;
}

const DashboardExitSlipModalWrapper: FC<Props> = ({
  request,
  enabled = true,
  refetchInitialization,
  onFailure,
  sessionId,
  classExitSlipId,
  setLoading,
  setExitSlipResult,
  skillId,
}) => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  useSuspiciousActivityTracking({ exitSlipSessionId: sessionId, skillId });
  useExitFullscreenOnUnmount();
  useCopyProtection();
  const checkTokenRefresh = async () => {
    if (isTokenExpired()) {
      await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog);
    }
  };

  const { status } = useLearnosityItemsInitialization({
    reloadOn10001AfterReady: true,
    initObj: request,
    enabled,
    assessmentType: 'exit_slip',
    refetchInitialization,
    onFailure,
    getContainer: () => document.getElementById('learnosity_assess'),
    onReady: itemsApp => {
      setLoading(false);
      itemsApp.on('test:start', function () {
        checkTokenRefresh();
      });
      itemsApp.on('button:saveQuit:clicked', function () {
        checkTokenRefresh();
        itemsApp.save();
      });
      itemsApp.on('item:changed', () => {
        checkTokenRefresh();
        itemsApp.save();
      });
      itemsApp.on('test:submit:success', async function () {
        posthog.capture('completed_exit_slip');
        const sentryTraceId =
          Sentry.getActiveSpan()?.spanContext().traceId ?? Sentry.getCurrentScope().getPropagationContext().traceId;
        assessmentSubmitted(
          sessionId,
          'exitslip',
          getAccessTokenSilently,
          logout,
          posthog,
          classExitSlipId,
          sentryTraceId,
          request.request.user_id,
        );
        itemsApp.getScores(scores => {
          setExitSlipResult(
            Object.values(itemsApp.getItems())
              .map(item =>
                (item.response_ids ?? []).reduce(
                  (acc, responseId) => {
                    const score = scores[responseId];
                    if (!score) return acc;
                    return { score: acc.score + (score.score ?? 0), maxScore: acc.maxScore + score.max_score };
                  },
                  { score: 0, maxScore: 0 },
                ),
              )
              .filter(item => item.maxScore > 0),
          );
        });
      });
    },
  });
  useEffect(() => {
    if (status !== 'idle') setLoading(status === 'initializing' || status === 'retry_wait');
  }, [status, setLoading]);

  return (
    <div>
      <div id="learnosity_assess" className="!tw-relative tw-z-[2]"></div>
    </div>
  );
};

export default DashboardExitSlipModalWrapper;
